/**
 * Update / skill-update nudges — the FOREGROUND half of the notifier (the background half is
 * version-refresh.ts).
 *
 * The load-bearing guarantee: this path NEVER does synchronous/awaited network I/O. It shows the
 * nudge from the on-disk cache only (instant), and when a cache is stale it hands the npm/GitHub
 * lookup to a DETACHED, unref'd child process (`<cli> __refresh-version-cache`) that writes the
 * cache for the NEXT run. So the current command is never slowed by the network. Extracted from the
 * entry module so this contract is unit-testable (see update-nudge.test.ts).
 */

import { spawn } from 'node:child_process';
import { isNewerVersion } from './utils.js';
import {
  CLI_CACHE_FILE,
  SKILL_CACHE_FILE,
  ADMIN_SKILL_CACHE_FILE,
  readVersionCache,
  isVersionCacheStale,
  touchVersionCache,
} from './version-cache.js';
import { findInstalledSkills, lowestInstalledVersion, ADMIN_SKILL_INSTALL_PATHS } from './skill-version.js';
import { findRepoRootSync } from './workspace.js';

// Hidden command + flags shared with the entry module's background-refresh dispatch (so the names
// can't drift between producer here and consumer in index.ts).
export const REFRESH_COMMAND = '__refresh-version-cache';
export const ENV_REFRESH_CLI = 'WAYAI_REFRESH_CLI';
export const ENV_REFRESH_SKILL = 'WAYAI_REFRESH_SKILL';
export const ENV_REFRESH_ADMIN_SKILL = 'WAYAI_REFRESH_ADMIN_SKILL';

function isUpdateCheckDisabled(): boolean {
  return Boolean(
    process.env.NO_UPDATE_NOTIFIER ||
    process.env.WAYAI_NO_UPDATE_CHECK ||
    process.env.CI
  );
}

/**
 * Spawn the detached background refresher. It outlives this process, does the npm/GitHub lookups,
 * and writes the cache for the NEXT run — so the current command is never slowed by the network. A
 * sentinel touch first, so concurrent foreground runs within the TTL don't each spawn a refresher.
 * `entryPath` is the CLI entry to re-invoke (the caller passes its own `import.meta.url` path).
 */
function scheduleVersionRefresh(
  opts: { cli: boolean; skill: boolean; adminSkill: boolean },
  entryPath: string,
): void {
  try {
    if (opts.cli) touchVersionCache(CLI_CACHE_FILE);
    if (opts.skill) touchVersionCache(SKILL_CACHE_FILE);
    if (opts.adminSkill) touchVersionCache(ADMIN_SKILL_CACHE_FILE);
    spawn(process.execPath, [entryPath, REFRESH_COMMAND], {
      detached: true,
      stdio: 'ignore',
      windowsHide: true, // no flashing console window on Windows (fires on every command past the TTL)
      env: {
        ...process.env,
        [ENV_REFRESH_CLI]: opts.cli ? '1' : '',
        [ENV_REFRESH_SKILL]: opts.skill ? '1' : '',
        [ENV_REFRESH_ADMIN_SKILL]: opts.adminSkill ? '1' : '',
      },
    }).unref();
  } catch { /* never block the CLI on a failed background spawn */ }
}

/**
 * Show update nudges from cache only (instant, never blocks), then refresh any stale cache in the
 * background for the next run. Nudges go to stderr — suppressed when stderr isn't a TTY (pipes,
 * redirects, logs) so machine-consumed output stays clean.
 *
 * @param currentVersion the running CLI's version (from package.json)
 * @param entryPath the CLI entry path to re-invoke for the background refresh
 */
export function showUpdateNudges(currentVersion: string, entryPath: string): void {
  if (isUpdateCheckDisabled() || !process.stderr.isTTY) return;
  try {
    // CLI: nudge from the last-known cached version (any age — keep nudging until they update).
    const cliLatest = readVersionCache(CLI_CACHE_FILE)?.latest ?? null;
    if (cliLatest && isNewerVersion(cliLatest, currentVersion)) {
      console.error(`\nUpdate available: ${currentVersion} → ${cliLatest}\nRun \`wayai update\` to update.`);
    }

    // Skills: only relevant when a skill is installed in this project. Use the non-spawning
    // repo-root finder — this runs on every command, so it must not shell out to `git`.
    const repoRoot = findRepoRootSync() ?? process.cwd();

    // Public `wayai` skill — latest comes from a public source, so this nudges any user.
    const installs = findInstalledSkills(repoRoot);
    const localSkillVersion = installs.length > 0 ? lowestInstalledVersion(installs) : null;
    if (localSkillVersion) {
      const skillLatest = readVersionCache(SKILL_CACHE_FILE)?.latest ?? null;
      if (skillLatest && isNewerVersion(skillLatest, localSkillVersion)) {
        console.error(`\nSkill update available: ${localSkillVersion} → ${skillLatest}\nRun \`npx skills add wayai-pro/wayai-skill -y\` to update.`);
      }
    }

    // Private `wayai-admin` skill — self-scopes to admins: install is platform-admin gated (so the
    // files are only here for admins), and the latest version is learned via that same gated
    // endpoint (403s for non-admins → cache stays empty → no nudge). Re-running install updates it.
    const adminInstalls = findInstalledSkills(repoRoot, ADMIN_SKILL_INSTALL_PATHS);
    const localAdminVersion = adminInstalls.length > 0 ? lowestInstalledVersion(adminInstalls) : null;
    if (localAdminVersion) {
      const adminLatest = readVersionCache(ADMIN_SKILL_CACHE_FILE)?.latest ?? null;
      if (adminLatest && isNewerVersion(adminLatest, localAdminVersion)) {
        console.error(`\nAdmin skill update available: ${localAdminVersion} → ${adminLatest}\nRun \`wayai admin skill install\` to update.`);
      }
    }

    // Refresh stale caches in the background (24h TTL). Skills are only worth refreshing when installed.
    const cliStale = isVersionCacheStale(CLI_CACHE_FILE);
    const skillStale = localSkillVersion !== null && isVersionCacheStale(SKILL_CACHE_FILE);
    const adminSkillStale = localAdminVersion !== null && isVersionCacheStale(ADMIN_SKILL_CACHE_FILE);
    if (cliStale || skillStale || adminSkillStale) {
      scheduleVersionRefresh({ cli: cliStale, skill: skillStale, adminSkill: adminSkillStale }, entryPath);
    }
  } catch { /* never block the CLI on update-check failures */ }
}
